import {
  LockClosedIcon,
  LockOpenIcon,
  PlayIcon,
  TrashIcon,
  UserCircleIcon,
  VariableIcon
} from '@heroicons/react/16/solid';
import {
  Button,
  Tooltip
} from '@platform/ui-library';
import {
  FC,
  useEffect,
  useRef,
  useState
} from 'react';
import { AudioController } from '@app/form_components/audio-controller';
import {
  useAsync,
  useDesign,
  useDesignRoles,
  useServices
} from '@app/hooks';
import {
  AudioType,
  defaultTtsVoice,
  DesignLanguage,
  DesignModule,
  DesignModuleNode,
  EDITOR_ROLES,
  PromptAudioUrls,
  TextToSpeechVoiceOrDefault
} from '@app/types';
import {
  getPromptText,
  isTextDynamic
} from '@app/utilities';
import {
  Message,
  MessageStatus
} from '../Message';
import { useAudioUrls } from './useAudioUrls';
import styles from './PromptTextAreas.module.scss';

const loadingMessage = 'Loading...';

const vtBlocksLockTip = (
  'A voice talent recording exists for this language. TTS previews can be generated to listen, but cannot be locked in as approved.'
);

const vtRecordingTip = (vtLabel: string | null) => (
  ((vtLabel) ? `${vtLabel} - submitted` : 'Submitted')
  + ' voice talent recording. This is used instead of TTS.'
);

export type PromptAudioControllerProps = {
  disabled?: boolean;
  language: DesignLanguage;
  node: DesignModuleNode;
  nodeModule: DesignModule;
  onUrlChange?: (newValue: PromptAudioUrls | null) => void;
  /**
   * Skips fetching from WFS to start with displaying recorded audio when available @default false.
   *
   * @note Recommended when many audio players are displayed at once.
   */
  skipInitialFetch?: boolean;
  ttsVoice?: TextToSpeechVoiceOrDefault;
};

export const PromptAudioController: FC<PromptAudioControllerProps> = ({
  disabled = false,
  language,
  node,
  nodeModule,
  onUrlChange,
  skipInitialFetch = false,
  ttsVoice
}) => {
  const serialized = useDesign();
  const {
    globalModalsService,
    textToPromptService,
    workflowService
  } = useServices();
  const { hasDesignRole } = useDesignRoles();
  const [message, setMessage] = useState<string | null>(null);
  const [playRequest, setPlayRequest] = useState<{ key: number; src: string } | null>(null);
  const [vtUrl, setVtUrl] = useState<string | null>(null);

  const { language: languageCode, voice: vtVoice } = language;
  // Per lang+TTS: other combos on this prompt may still be dynamic.
  const isDynamicPrompt = isTextDynamic({
    languageCode,
    node,
    serialized,
    ttsVoice
  });
  const {
    lockedUrl,
    persistAudioUrls,
    setLockedUrl,
    setUrl,
    updateEntry,
    url
  } = useAudioUrls({
    isDynamicPrompt,
    languageCode,
    node,
    onUrlChange,
    ttsVoice
  });
  const [vtVoices] = useAsync(() => {
    if (!vtVoice) {
      return Promise.resolve([]);
    }
    return workflowService.getVoices(AudioType.ttp, languageCode, serialized);
  }, [languageCode, serialized, vtVoice]);

  const vtLabel = (
    vtVoices?.find(({ value }) => value === vtVoice)?.label
    ?? vtVoice
    ?? null
  );
  const text = getPromptText({ languageCode, node, ttsVoice });
  const hasVtRecording = !!vtUrl;
  const isLoading = message === loadingMessage;
  const canFetchTts = !!ttsVoice && ttsVoice !== defaultTtsVoice && !!text.trim();
  // TTS-only lock; blocked when a VT recording exists for this language variant.
  const canToggleLock = !!url && !isDynamicPrompt && persistAudioUrls && !hasVtRecording;
  const isGenerateDisabled = disabled || !text.trim();
  const showGenerateButton = hasDesignRole(EDITOR_ROLES) && !disabled && !hasVtRecording;
  const promptIdentity = `${text}\0${ttsVoice ?? ''}\0${vtVoice ?? ''}`;
  const promptIdentityRef = useRef(promptIdentity);
  promptIdentityRef.current = promptIdentity;
  const requestIdentityRef = useRef<string | null>(null);
  // Keep player rows ordered by src so lock/unlock only moves icons, not players.
  const slotOrderRef = useRef<string[]>([]);
  const presentUrls = [...new Set([lockedUrl, url].filter((src) => src != null))];
  const keptSlots = slotOrderRef.current.filter((src) => presentUrls.includes(src));
  const slotOrder = [...keptSlots, ...presentUrls.filter((src) => !keptSlots.includes(src))];
  slotOrderRef.current = slotOrder;
  // VT recording is authoritative — hide TTS previews/locks even if audioUrls exist.
  const ttsSlots = (hasVtRecording) ? [] : slotOrder.map((src) => ({
    isLocked: src === lockedUrl,
    src
  }));
  const showControlRow = showGenerateButton || !!message;

  const generateAudio = async (doFetch = true) => {
    const requestIdentity = promptIdentityRef.current;
    if (isLoading && requestIdentity === requestIdentityRef.current) {
      return;
    }
    if (doFetch && !canFetchTts) {
      return;
    }
    if (!doFetch && !vtVoice && !canFetchTts) {
      return;
    }
    requestIdentityRef.current = requestIdentity;
    setMessage(loadingMessage);
    const { ttsUrl, vtUrl: nextVtUrl } = await textToPromptService.getAudios({
      doFetch,
      language,
      node,
      nodeModule,
      serialized
    });
    if (requestIdentityRef.current !== requestIdentity) {
      return;
    }
    if (promptIdentityRef.current !== requestIdentity) {
      setMessage(null);
      return;
    }
    setVtUrl(nextVtUrl);
    // Soft-load: skip writing TTS preview when already locked (still load VT).
    // Explicit Generate always updates the listen/preview URL.
    // VT recording wins — do not attach TTS previews alongside it.
    if (!nextVtUrl && canFetchTts && (doFetch || !lockedUrl)) {
      setUrl(ttsUrl);
      if (doFetch && ttsUrl) {
        setPlayRequest((prev) => ({
          key: (prev?.key ?? 0) + 1,
          src: ttsUrl
        }));
      }
    }
    setMessage(null);
  };

  const generateAudioRef = useRef(generateAudio);
  generateAudioRef.current = generateAudio;
  const textKeyRef = useRef(promptIdentity);
  const softLoadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const textChanged = textKeyRef.current !== promptIdentity;
    textKeyRef.current = promptIdentity;

    // Drop preview when the message changes — persisted audioUrls are not keyed by text.
    // Soft-load may restore a localStorage/WFS URL only when it matches the new text.
    if (textChanged && !lockedUrl) {
      softLoadedKeyRef.current = null;
      setMessage(null);
      setVtUrl(null);
      if (url) {
        setUrl(null);
        return;
      }
    }

    const needsSoftLoad = (
      !skipInitialFetch
      && softLoadedKeyRef.current !== promptIdentity
      && ((!lockedUrl && !url && canFetchTts) || (!!vtVoice && !vtUrl))
    );
    if (needsSoftLoad) {
      softLoadedKeyRef.current = promptIdentity;
      setMessage(null);
      generateAudioRef.current(false);
    }
  }, [canFetchTts, lockedUrl, promptIdentity, skipInitialFetch, url, vtUrl, vtVoice]);

  const promoteCandidateToLocked = () => {
    if (!canToggleLock || !url) {
      return;
    }
    // Swap when a locked recording already exists — keep the prior lock as the alt preview.
    if (lockedUrl && lockedUrl !== url) {
      updateEntry({
        lockedUrl: url,
        url: lockedUrl
      });
    } else {
      setLockedUrl(url);
    }
  };

  const removeFailedAudio = () => {
    textToPromptService.removeAudioFromLocalStorage(language, node);
    setUrl(null);
    setMessage('There was a problem trying to load the audio file. Please try generating it again.');
  };

  const unlockAudio = async () => {
    if (!lockedUrl) {
      return;
    }
    // Keep the unlocked recording; discard any distinct preview candidate.
    if (url && url !== lockedUrl) {
      const discardUrl = url;
      const proceed = await globalModalsService.openConfirmationModal({
        children: (
          <div className="box content">
            <p>
              Unlocking keeps the locked recording as a preview. This preview
              will be discarded:
            </p>
            <AudioController url={discardUrl} />
          </div>
        ),
        labels: {
          continueButton: 'Unlock',
          header: 'Discard preview recording?'
        }
      });
      if (!proceed) {
        return;
      }
    }
    updateEntry({
      lockedUrl: null,
      url: lockedUrl
    });
  };

  if (!showControlRow && !hasVtRecording && ttsSlots.length < 1) {
    return null;
  }

  return (
    <div className={styles.audioControllerWrapper}>
      {(!hasVtRecording) ? null : (
        <div className={styles.playerGroup}>
          <div className={styles.playerRow}>
            <Button
              aria-label={vtRecordingTip(vtLabel)}
              className={styles.lockButtonLocked}
              disabled
              icon={UserCircleIcon}
              invert
              showTooltipIfDisabled
              theme={['round', 'small', 'color']}
              tooltip={vtRecordingTip(vtLabel)}
              type="button"
            />
            <AudioController url={vtUrl} />
          </div>
        </div>
      )}
      {(ttsSlots.length < 1) ? null : (
        <div className={styles.playerGroup}>
          {ttsSlots.map(({ isLocked, src }) => (
            <div className={styles.playerRow} key={src}>
              {(isLocked) ? (
                <Tooltip tooltip="This recording is locked in as approved. Click to unlock and edit the prompt text." invert>
                  <Button
                    aria-label="Unlock this recording"
                    className={styles.lockButtonLocked}
                    icon={LockClosedIcon}
                    onClick={unlockAudio}
                    theme={['round', 'small', 'color']}
                    type="button"
                  />
                </Tooltip>
              ) : (canToggleLock) ? (
                <Tooltip invert tooltip="Lock this preview recording in as the approved version.">
                  <Button
                    aria-label="Lock this recording in as approved"
                    className={styles.lockButtonUnlocked}
                    icon={LockOpenIcon}
                    onClick={promoteCandidateToLocked}
                    theme={['round', 'small', 'outline']}
                    type="button"
                  />
                </Tooltip>
              ) : (isDynamicPrompt) ? (
                <Button
                  aria-label="Ephemeral preview for dynamic prompt"
                  className={styles.lockButtonUnlocked}
                  disabled
                  icon={VariableIcon}
                  invert
                  showTooltipIfDisabled
                  theme={['round', 'small', 'outline']}
                  tooltip="Recordings for dynamic prompts cannot be locked in as approved"
                  type="button"
                />
              ) : (hasVtRecording) ? (
                <Button
                  aria-label="Cannot lock while voice talent recording exists"
                  className={styles.lockButtonUnlocked}
                  disabled
                  icon={LockOpenIcon}
                  invert
                  showTooltipIfDisabled
                  theme={['round', 'small', 'outline']}
                  tooltip={vtBlocksLockTip}
                  type="button"
                />
              ) : (
                <span className={styles.lockIconPlaceholder} />
              )}
              <AudioController
                autoPlay={playRequest?.src === src}
                autoPlayKey={(playRequest?.src === src) ? playRequest.key : 0}
                url={src}
                onError={removeFailedAudio}
              />
              {(isLocked) ? null : (
                <Button
                  aria-label="Discard this preview recording"
                  color={{
                    background: 'transparent',
                    border: 'transparent',
                    text: 'currentColor'
                  }}
                  icon={TrashIcon}
                  invert
                  onClick={() => setUrl(null)}
                  theme={['round', 'small', 'outline']}
                  tooltip="Discard this preview recording"
                  type="button"
                />
              )}
            </div>
          ))}
        </div>
      )}
      {(!showControlRow) ? null : (
        <div className={styles.audioController}>
          <div className={styles.message}>
            {(!message) ? null : (
              <Message
                message={message}
                status={(isLoading) ? MessageStatus.loading : MessageStatus.clear}
                variant="inline"
              />
            )}
          </div>
          {(!showGenerateButton) ? null : (
            <Button
              disabled={isGenerateDisabled}
              icon={PlayIcon}
              onClick={() => generateAudio()}
              style={{ flexShrink: '0' }}
              theme={(isGenerateDisabled) ? ['outline', 'small'] : ['outlineHighlight', 'small']}
              type="button"
            >
              Generate audio
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
